import { ClassicPreset, type GetSchemes, NodeEditor } from 'rete';
import '../../../../modules/workflow/components/white-node';
import '../../../../modules/workflow/components/white-socket';
import '../../../../modules/workflow/components/white-connection';
import '../../../../modules/workflow/components/task-group-visual';
import {
  type LitArea2D,
  LitPlugin,
  Presets as LitPresets,
} from '@retejs/lit-plugin';
import { curveCatmullRom } from 'd3-shape';
import { html } from 'lit';
import {
  type Area2D,
  AreaExtensions,
  AreaPlugin,
  Zoom,
} from 'rete-area-plugin';
import {
  Presets as ArrangePresets,
  AutoArrangePlugin,
} from 'rete-auto-arrange-plugin';
import { ConnectionPathPlugin } from 'rete-connection-path-plugin';
import {
  ConnectionPlugin,
  Presets as ConnectionPresets,
} from 'rete-connection-plugin';
import {
  type ContextMenuExtra,
  ContextMenuPlugin,
} from 'rete-context-menu-plugin';
import type { Items } from 'rete-context-menu-plugin/_types/types';
import { setupCustomContextMenu } from '../../../../modules/workflow/components/custom-context-menu';
import type { Connection } from './connection';
import { TaskGroupNode } from './task-group-node';
import type { TaskNode } from './task-node';

export type Node = TaskNode | TaskGroupNode;
export type Conn = Connection<TaskNode, TaskNode>;
export type Schemes = GetSchemes<Node, Conn>;
export type AreaExtra = Area2D<Schemes> | LitArea2D<Schemes> | ContextMenuExtra;
// Keep in sync with the background grid spacing defined in CSS.
export const WORKFLOW_GRID_SIZE = 16;
const WHEEL_ZOOM_INTENSITY = 0.03;

function createAccumulateOnCtrl() {
  let pressed = false;
  const keydown = (e: KeyboardEvent) => {
    if (e.key === 'Control' || e.key === 'Meta') pressed = true;
  };
  const keyup = (e: KeyboardEvent) => {
    if (e.key === 'Control' || e.key === 'Meta') pressed = false;
  };
  document.addEventListener('keydown', keydown);
  document.addEventListener('keyup', keyup);
  return {
    active: () => pressed,
    destroy: () => {
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('keyup', keyup);
    },
  };
}

export function setupPlugins(container: HTMLElement, items: Items<Schemes>) {
  const editor = new NodeEditor<Schemes>();
  const area = new AreaPlugin<Schemes, AreaExtra>(container);
  const connection = new ConnectionPlugin<Schemes, AreaExtra>();
  const litRender = new LitPlugin<Schemes, AreaExtra>();

  const pathPlugin = new ConnectionPathPlugin<Schemes, Area2D<Schemes>>({
    curve: () => curveCatmullRom,
    arrow: () => ({
      color: 'var(--color-connection-stroke)',
      marker: 'M8,-6 L8,6 L20,0 z',
    }),
  });

  litRender.use(pathPlugin);

  editor.use(area);
  area.area.setZoomHandler(new Zoom(WHEEL_ZOOM_INTENSITY));

  area.use(litRender);
  area.use(connection);

  connection.addPreset(ConnectionPresets.classic.setup());

  litRender.addPreset(
    LitPresets.classic.setup({
      customize: {
        node(data) {
          if (data.payload instanceof TaskGroupNode) {
            return () =>
              html`<kv-task-group
                .data=${data.payload}
              ></kv-task-group>`;
          }
          return ({ emit }) =>
            html`<white-node .data=${data.payload} .emit=${emit}></white-node>`;
        },
        socket(data) {
          return () => html`<white-socket .data=${data}></white-socket>`;
        },
        connection(data) {
          return ({ path }) =>
            html`<white-connection
              .path=${path}
              .connection=${data.payload}
              .selected=${Boolean((data.payload as any)?.selected)}
            ></white-connection>`;
        },
      },
    })
  );
  // Enable context menu rendering via custom preset
  litRender.addPreset(setupCustomContextMenu({ delay: 1000 }));

  const arrange = new AutoArrangePlugin<Schemes>();

  arrange.addPreset(ArrangePresets.classic.setup());

  area.use(arrange);

  AreaExtensions.simpleNodesOrder(area);
  AreaExtensions.snapGrid(area, { size: WORKFLOW_GRID_SIZE, dynamic: false });

  type SelectorEntity = {
    label: string;
    id: string;
    unselect(): void;
    translate(dx: number, dy: number): void;
  };

  const dispatchSelectionChange = (entities: Map<string, SelectorEntity>) => {
    const rawIds = Array.from(entities.values()).map((e) => (e as any).id);
    const tasks: number[] = [];
    const groups: Array<number | string> = [];
    for (const rawId of rawIds) {
      const strId = String(rawId);
      if (strId.startsWith('group-')) {
        const key = strId.slice('group-'.length);
        const num = Number(key);
        groups.push(Number.isFinite(num) ? num : key);
      } else {
        const numId = Number(rawId);
        if (Number.isFinite(numId)) tasks.push(numId);
      }
    }
    const detail = { ids: tasks, groupIds: groups, rawIds };
    container.dispatchEvent(
      new CustomEvent('tasks-selection-changed', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  };

  class MySelector<
    E extends SelectorEntity,
  > extends AreaExtensions.Selector<E> {
    async add(entity: E, accumulate: boolean): Promise<void> {
      await super.add(entity, accumulate);
      dispatchSelectionChange(this.entities);
    }
    async remove(entity: E): Promise<void> {
      await super.remove(entity);
      dispatchSelectionChange(this.entities);
      if (this.entities.size === 0) {
        container.dispatchEvent(
          new CustomEvent('selection-cleared', {
            bubbles: true,
            composed: true,
          })
        );
      }
    }
    async unselectAll(): Promise<void> {
      const hadSelection = this.entities.size > 0;
      await super.unselectAll();
      if (hadSelection) {
        dispatchSelectionChange(this.entities);
        container.dispatchEvent(
          new CustomEvent('selection-cleared', {
            bubbles: true,
            composed: true,
          })
        );
      }
    }
  }

  const selector = new MySelector();
  const accumulating = createAccumulateOnCtrl();
  AreaExtensions.selectableNodes(area, selector, { accumulating });

  container.classList.add('rete-area');

  const contextMenu = new ContextMenuPlugin<Schemes>({ items });
  area.use(contextMenu);

  return {
    editor,
    area,
    connection,
    litRender,
    arrange,
    contextMenu,
    accumulating,
  };
}

export class LabeledInputControl<
  T extends 'text' | 'number',
  N = T extends 'text' ? string : number,
> extends ClassicPreset.InputControl<T, N> {
  public label?: string;
}
